import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import c from '../../constant';
import useStore from '../../store';
import getPositionLabel from '../../utils/getPositionLabel';
import getOfficeLabel from '../../utils/getOfficeLabel';
import getShortDepartmentLabel from '../../utils/getShortDepartmentLabel';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const toStr = (value) => String(value ?? '');

export default function ExportExcelTrainingRecord({ records = [], ...props }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);

	const filename = lang === 'en' ? 'Training_Record' : 'កំណត់ត្រាវគ្គបណ្តុះបណ្តាល';

	const data = useMemo(() => {
		return (
			records?.map((item) => {
				return {
					no: item?.no ? String(item?.no).padStart(4, '0') : '',
					officials: toStr(lang === 'kh' ? item?.user?.fullNameKhmer : item?.user?.fullName),
					department: toStr(t(getShortDepartmentLabel(item?.user?.department))),
					office: toStr(t(getOfficeLabel(item?.user?.office))),
					position: toStr(t(lang === 'kh' ? getPositionLabel(item?.user?.position) : getPositionLabel(item?.user?.position, 'en'))),
					courseTitle: toStr(item?.courseTitle),
					organiser: toStr(item?.organiser),
					location: toStr(item?.location),
					dateStart: item?.dateStart
						? moment(item?.dateStart)
								.locale(lang === 'kh' ? 'km' : 'en-gb')
								.format('DD MMMM YYYY')
						: '',
					dateEnd: item?.dateEnd
						? moment(item?.dateEnd)
								.locale(lang === 'kh' ? 'km' : 'en-gb')
								.format('DD MMMM YYYY')
						: '',
					means: toStr(t(item?.means)),
					type: toStr(t(item?.type)),
					locationType: toStr(t(item?.locationType)),
					bioListing: item?.bioListing === true ? t('Listed') : t('Not Listed'),
					createdAt: item?.createdAt
						? moment(item?.createdAt)
								.locale(lang === 'kh' ? 'km' : 'en-gb')
								.format('YYYY-MM-DD HH:mm')
						: '',
				};
			}) || []
		);
	}, [records, lang, t]);

	const localData = useMemo(() => {
		return data?.filter((item) => item?.locationType === 'LOCAL' || item?.locationType === 'ក្នុងស្រុក');
	}, [data]);

	const overseasData = useMemo(() => {
		return data?.filter((item) => item?.locationType === 'OVERSEAS' || item?.locationType === 'ក្រៅប្រទេស');
	}, [data]);

	return (
		<Box {...props}>
			<ExcelFile
				filename={
					lang === 'en'
						? `${filename}_Generated_At_${moment().format(c.excelDateTimeFormat)}`
						: `${filename}_បង្កើតនៅថ្ងៃ_${moment().format(c.excelDateTimeFormat)}`
				}
				element={
					<Button
						type="button"
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
						isDisabled={!records?.length}
					>
						{t('Export')}
					</Button>
				}
			>
				{/* ALL RECORDS */}
				<ExcelSheet data={data} name={t('Training Record')}>
					<ExcelColumn label={t('Number')} value="no" />
					<ExcelColumn label={t('Officials')} value="officials" />
					<ExcelColumn label={t('Department')} value="department" />
					<ExcelColumn label={t('Office')} value="office" />
					<ExcelColumn label={t('Position')} value="position" />
					<ExcelColumn label={t('Course Title')} value="courseTitle" />
					<ExcelColumn label={t('Organiser')} value="organiser" />
					<ExcelColumn label={t('dateFrom')} value="dateStart" />
					<ExcelColumn label={t('dateTo')} value="dateEnd" />
					<ExcelColumn label={t('location')} value="location" />
					<ExcelColumn label={t('Means')} value="means" />
					<ExcelColumn label={t('Location Type')} value="locationType" />
					<ExcelColumn label={t('Type')} value="type" />
					<ExcelColumn label={t('Bio Listing')} value="bioListing" />
					<ExcelColumn label={t('Created At')} value="createdAt" />
				</ExcelSheet>

				{/* LOCAL */}
				<ExcelSheet data={localData} name={t('LOCAL')}>
					<ExcelColumn label={t('Number')} value="no" />
					<ExcelColumn label={t('Officials')} value="officials" />
					<ExcelColumn label={t('Department')} value="department" />
					<ExcelColumn label={t('Office')} value="office" />
					<ExcelColumn label={t('Position')} value="position" />
					<ExcelColumn label={t('Course Title')} value="courseTitle" />
					<ExcelColumn label={t('Organiser')} value="organiser" />
					<ExcelColumn label={t('dateFrom')} value="dateStart" />
					<ExcelColumn label={t('dateTo')} value="dateEnd" />
					<ExcelColumn label={t('location')} value="location" />
					<ExcelColumn label={t('Means')} value="means" />
					<ExcelColumn label={t('Location Type')} value="locationType" />
					<ExcelColumn label={t('Type')} value="type" />
					<ExcelColumn label={t('Bio Listing')} value="bioListing" />
					<ExcelColumn label={t('Created At')} value="createdAt" />
				</ExcelSheet>

				{/* OVERSEAS */}
				<ExcelSheet data={overseasData} name={t('OVERSEAS')}>
					<ExcelColumn label={t('Number')} value="no" />
					<ExcelColumn label={t('Officials')} value="officials" />
					<ExcelColumn label={t('Department')} value="department" />
					<ExcelColumn label={t('Office')} value="office" />
					<ExcelColumn label={t('Position')} value="position" />
					<ExcelColumn label={t('Course Title')} value="courseTitle" />
					<ExcelColumn label={t('Organiser')} value="organiser" />
					<ExcelColumn label={t('dateFrom')} value="dateStart" />
					<ExcelColumn label={t('dateTo')} value="dateEnd" />
					<ExcelColumn label={t('location')} value="location" />
					<ExcelColumn label={t('Means')} value="means" />
					<ExcelColumn label={t('Location Type')} value="locationType" />
					<ExcelColumn label={t('Type')} value="type" />
					<ExcelColumn label={t('Bio Listing')} value="bioListing" />
					<ExcelColumn label={t('Created At')} value="createdAt" />
				</ExcelSheet>
			</ExcelFile>
		</Box>
	);
}
